import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, Clock, BookOpen } from "lucide-react";
import Reveal from "../Reveal";
import { useLanguage } from "../../contexts/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function BlogPreviewStrip() {
  const { lang } = useLanguage();
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    let cancelled = false;
    let retryTimer = null;

    const fetch = (attempt = 0) => {
      axios.get(`${BACKEND_URL}/api/blog/articles`, { params: { lang, limit: 3 } })
        .then((r) => {
          if (cancelled) return;
          const data = (r.data?.articles || []).slice(0, 3);
          if (data.length === 0 && attempt < 3) {
            retryTimer = setTimeout(() => fetch(attempt + 1), 2500);
          } else {
            setArticles(data);
          }
        })
        .catch(() => {});
    };

    fetch();
    return () => { cancelled = true; if (retryTimer) clearTimeout(retryTimer); };
  }, [lang]);

  if (articles.length === 0) return null;

  const labels = {
    tr: { eyebrow: "SON YAZILAR", title: "Dijital pazarlama", accent: "rehberimiz.", cta: "Tüm Yazıları Gör", read: "dk okuma" },
    en: { eyebrow: "LATEST POSTS", title: "Our digital marketing", accent: "guide.", cta: "See All Posts", read: "min read" },
    es: { eyebrow: "ÚLTIMAS ENTRADAS", title: "Nuestra guía de", accent: "marketing digital.", cta: "Ver Todo", read: "min lectura" },
    de: { eyebrow: "NEUESTE BEITRÄGE", title: "Unser digitaler Marketing-", accent: "Leitfaden.", cta: "Alle anzeigen", read: "Min. Lesen" },
    fr: { eyebrow: "DERNIERS ARTICLES", title: "Notre guide du", accent: "marketing digital.", cta: "Voir Tout", read: "min lecture" },
    ar: { eyebrow: "أحدث المقالات", title: "دليل", accent: "التسويق الرقمي.", cta: "عرض الكل", read: "دقائق قراءة" },
  };
  const l = labels[lang] || labels.en;

  return (
    <section data-testid="blog-preview-strip" className="bg-cream py-section border-t border-mist">
      <div className="container-page">
        <Reveal as="div" className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
          <div>
            <span className="eyebrow"><BookOpen className="h-3 w-3 text-gold" />{l.eyebrow}</span>
            <h2 className="mt-4 font-display text-display-2 font-semibold text-ink">
              {l.title} <span className="gold-foil-text">{l.accent}</span>
            </h2>
          </div>
          <Link to={`/${lang}/blog`}
            className="inline-flex items-center gap-2 rounded-full border-2 border-ink/20 px-5 py-2.5 text-sm font-semibold text-ink hover:border-ink hover:bg-ink hover:text-cream transition-colors self-start sm:self-auto">
            {l.cta} <ArrowUpRight className="h-4 w-4" />
          </Link>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          {articles.map((a, i) => {
            const tr = a.translations || {};
            return (
              <Reveal key={a.slug || i} as="div" delay={i * 0.06}>
                <Link to={`/${lang}/blog/${a.slug}`}
                  className="group block rounded-2xl border border-mist bg-white overflow-hidden hover:border-brand-pink/40 hover:shadow-card transition-all duration-300 h-full flex flex-col">
                  {a.cover_image_url ? (
                    <div className="h-44 overflow-hidden bg-mist">
                      <img src={a.cover_image_url} alt={tr.title} loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    </div>
                  ) : (
                    <div className="h-44 bg-gradient-to-br from-brand-pink/15 to-brand-magenta/15 flex items-center justify-center">
                      <BookOpen className="h-10 w-10 text-brand-pink/40" />
                    </div>
                  )}
                  <div className="p-5 flex-1 flex flex-col">
                    <span className="text-[0.6rem] uppercase tracking-[0.18em] font-bold text-brand-pink-deep">{a.category}</span>
                    <h3 className="mt-2 font-display text-h3 font-semibold text-ink leading-snug line-clamp-2 flex-1">
                      {tr.title || a.slug}
                    </h3>
                    <div className="mt-3 flex items-center justify-between">
                      <span className="inline-flex items-center gap-1 text-xs text-ink/50">
                        <Clock className="h-3 w-3" />{a.reading_time_min || 5} {l.read}
                      </span>
                      <ArrowUpRight className="h-4 w-4 text-ink/40 group-hover:text-brand-pink transition-colors" />
                    </div>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
